import { useState, useCallback, useRef  } from 'react';
import { X, Plus, Trash2, GripVertical, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';

interface WorkflowStatus {
  id: string;
  name: string;
  color: string;
  category: string;
}

interface FormData {
  name: string;
  description: string;
  key: string;
  organization_id: string;
  workflow: {
    statuses: WorkflowStatus[];
  };
}

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<FormData, 'workflow'> & {
    workflow: { statuses: Omit<WorkflowStatus, 'id'>[] }
  }) => Promise<void>;
  organizationId: string;
}

// static templates for refernce
const TEMPLATES = [
  {
    label: 'Software Dev',
    workflow: 'Software Development Workflow',
    statuses: [
      { name: 'TODO',                   color: '#6B7280', category: 'todo' },
      { name: 'DEV IN PROGRESS',        color: '#3B82F6', category: 'development' },
      { name: 'CODE REVIEW',            color: '#8B5CF6', category: 'development' },
      { name: 'QA IN PROGRESS',         color: '#F59E0B', category: 'testing' },
      { name: 'QA COMPLETED',           color: '#10B981', category: 'testing' },
      { name: 'DEPLOYMENT IN PROGRESS', color: '#EF4444', category: 'deployment' },
      { name: 'DONE',                   color: '#059669', category: 'done' },
    ],
  },
  {
    label: 'Textile',
    workflow: 'Textile Manufacturing Workflow',
    statuses: [
      { name: 'ORDER RECEIVED',      color: '#6B7280', category: 'order' },
      { name: 'YARN SAMPLING',       color: '#8B5CF6', category: 'sampling' },
      { name: 'STITCHING',           color: '#3B82F6', category: 'stitching' },
      { name: 'CHECKING DAMAGES',    color: '#EF4444', category: 'quality' },
      { name: 'DYEING',              color: '#F59E0B', category: 'dyeing' },
      { name: 'PACKING',             color: '#F97316', category: 'packing' },
      { name: 'DELIVERY COMPLETED',  color: '#059669', category: 'done' },
    ],
  },
  {
    label: 'Sales Pipeline',
    workflow: 'B2B Sales Workflow',
    statuses: [
      { name: 'LEAD GENERATED',    color: '#6B7280', category: 'prospecting' },
      { name: 'DISCOVERY CALL',    color: '#3B82F6', category: 'qualification' },
      { name: 'PROPOSAL SENT',     color: '#F59E0B', category: 'proposal' },
      { name: 'NEGOTIATION',       color: '#F97316', category: 'closing' },
      { name: 'CLOSED WON',        color: '#059669', category: 'done' },
      { name: 'CLOSED LOST',       color: '#111827', category: 'archive' },
    ],
  },
];

const COLOR_SWATCHES = [
  '#6B7280', '#3B82F6', '#8B5CF6', '#10B981',
  '#F59E0B', '#EF4444', '#EC4899', '#F97316',
  '#059669', '#0EA5E9', '#6366F1', '#111827',
];

const uid = () => Math.random().toString(36).slice(2, 9);

const makeStatus = (overrides?: Partial<WorkflowStatus>): WorkflowStatus => ({
  id: uid(),
  name: '',
  color: '#6B7280',
  category: '',
  ...overrides,
});

export const CreateProjectModal = ({
  isOpen,
  onClose,
  onSubmit,
  organizationId,
}: CreateProjectModalProps) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showWorkflow, setShowWorkflow] = useState(true);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [colorPickerIdx, setColorPickerIdx] = useState<number | null>(null);
  const colorInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [form, setForm] = useState<FormData>({
    name: '',
    description: '',
    key: '',
    organization_id: organizationId,
    workflow: {
      statuses: [
        makeStatus({ name: 'TODO',        color: '#6B7280', category: 'todo' }),
        makeStatus({ name: 'IN PROGRESS', color: '#3B82F6', category: 'in_progress' }),
        makeStatus({ name: 'DONE',        color: '#059669', category: 'done' }),
      ],
    },
  });

  const handleNameChange = (name: string) => {
    const key = name
      .toUpperCase()
      .replace(/[^A-Z\s]/g, '')
      .split(' ')
      .filter(Boolean)
      .map((w) => w[0])
      .join('')
      .slice(0, 6);
    setForm((f) => ({ ...f, name, key }));
    setErrors((e) => ({ ...e, name: '', key: '' }));
  };

  const validateStepOne = () => {
    const errs: Record<string, string> = {};
    if (!form.name.trim() || form.name.trim().length < 3)
      errs.name = 'Project name must be at least 3 characters';
    if (!form.key || form.key.length < 2)
      errs.key = 'Key must be 2–10 uppercase letters';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };


  const validateStepTwo = () => {
    const errs: Record<string, string> = {};
    if (form.workflow.statuses.length === 0)
      errs.statuses = 'Add at least one status';
    form.workflow.statuses.forEach((s, i) => {
      if (!s.name.trim()) errs[`status_${i}`] = 'Status name required';
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (validateStepOne()) setStep(2);
  };

  const handleBack = () => {
    setStep(1);
    setErrors({});
  };

  const handleSubmit = async () => {
    if (!validateStepTwo()) return;
    setIsSubmitting(true);
    try {
      await onSubmit({
        name: form.name,
        description: form.description,
        key: form.key,
        organization_id: organizationId,
        workflow: {
          statuses: form.workflow.statuses.map(({ id, ...rest }) => rest),
        },
      });
      handleClose();
    } catch (err: any) {
      setErrors({ submit: err?.message || 'Failed to create project' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setErrors({});
    setColorPickerIdx(null);
    setForm({
      name: '', description: '', key: '',
      organization_id: organizationId,
      workflow: {
        statuses: [
          makeStatus({ name: 'TODO',        color: '#6B7280', category: 'todo' }),
          makeStatus({ name: 'IN PROGRESS', color: '#3B82F6', category: 'in_progress' }),
          makeStatus({ name: 'DONE',        color: '#059669', category: 'done' }),
        ],
      },
    });
    onClose();
  };

  const addStatus = () => {
    setForm((f) => ({
      ...f,
      workflow: { ...f.workflow, statuses: [...f.workflow.statuses, makeStatus()] },
    }));
  };

  const removeStatus = (idx: number) => {
    setForm((f) => ({
      ...f,
      workflow: {
        ...f.workflow,
        statuses: f.workflow.statuses.filter((_, i) => i !== idx),
      },
    }));
    setErrors((e) => {
      const next = { ...e };
      delete next[`status_${idx}`];
      return next;
    });
  };

  const updateStatus = (idx: number, field: keyof WorkflowStatus, value: string) => {
    setForm((f) => ({
      ...f,
      workflow: {
        ...f.workflow,
        statuses: f.workflow.statuses.map((s, i) =>
          i === idx ? { ...s, [field]: value } : s
        ),
      },
    }));
    setErrors((e) => ({ ...e, [`status_${idx}`]: '' }));
  };

  const handleDragStart = (idx: number) => setDragIdx(idx);
  const handleDragOver = useCallback(
    (e: React.DragEvent, idx: number) => {
      e.preventDefault();
      if (dragIdx === null || dragIdx === idx) return;
      setForm((f) => {
        const statuses = [...f.workflow.statuses];
        const [moved] = statuses.splice(dragIdx, 1);
        statuses.splice(idx, 0, moved);
        return { ...f, workflow: { ...f.workflow, statuses } };
      });
      setDragIdx(idx);
    },
    [dragIdx]
  );
  const handleDragEnd = () => setDragIdx(null);

  const applyTemplate = (tpl: typeof TEMPLATES[0]) => {
    setForm((f) => ({
      ...f,
      workflow: {
        name: tpl.workflow,
        statuses: tpl.statuses.map((s) => makeStatus(s)),
      },
    }));
    setErrors({});
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Create New Project</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {step === 1 ? 'Step 1 of 2 — Project details' : 'Step 2 of 2 — Workflow setup'}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex px-6 pt-4 gap-2">
          {[1, 2].map((s) => (
            <div
              key={s}
              className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                s <= step ? 'bg-blue-500' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {step === 1 && (
            <div className="space-y-5">

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Project Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g. Sales Opportunity Pipeline"
                  className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    errors.name ? 'border-red-400 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                  }`}
                  maxLength={200}
                  autoFocus
                />
                {errors.name && (
                  <p className="mt-1.5 text-xs text-red-500">⚠ {errors.name}</p>
                )}
              </div>

              {/* Key */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Project Key <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={form.key}
                    onChange={(e) => {
                      const v = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 10);
                      setForm((f) => ({ ...f, key: v }));
                      setErrors((er) => ({ ...er, key: '' }));
                    }}
                    placeholder="PROJ"
                    className={`w-full px-4 py-2.5 border rounded-lg text-sm font-mono tracking-widest uppercase focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      errors.key ? 'border-red-400 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                    }`}
                    maxLength={10}
                  />
                  {form.key && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                      e.g. {form.key}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  2–10 uppercase letters. Auto-generated from project name.
                </p>
                {errors.key && (
                  <p className="mt-1 text-xs text-red-500">⚠ {errors.key}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Description
                  <span className="text-gray-400 font-normal ml-1">(optional)</span>
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Describe what this project is about..."
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 transition-all"
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">

              {/* Workflow name */}

              {/* Sample templates */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  Quick templates
                </p>
                <div className="flex flex-wrap gap-2">
                  {TEMPLATES.map((tpl) => (
                    <button
                      key={tpl.label}
                      type="button"
                      onClick={() => applyTemplate(tpl)}
                      className="px-3 py-1.5 text-xs font-medium rounded-full border border-gray-200 text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all"
                    >
                      {tpl.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <button
                    type="button"
                    onClick={() => setShowWorkflow((v) => !v)}
                    className="flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                  >
                    {showWorkflow
                      ? <ChevronUp className="w-4 h-4" />
                      : <ChevronDown className="w-4 h-4" />}
                    Workflow Statuses
                    <span className="text-xs font-normal text-gray-400 ml-1">
                      ({form.workflow.statuses.length})
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={addStatus}
                    className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 px-2.5 py-1.5 rounded-md hover:bg-blue-50 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add status
                  </button>
                </div>

                {errors.statuses && (
                  <p className="mb-2 text-xs text-red-500">⚠ {errors.statuses}</p>
                )}

                {showWorkflow && (
                  <>
                    <div className="flex items-center gap-2 px-2.5 mb-1">
                      <div className="w-4" />
                      <div className="w-6 text-xs text-gray-400 text-center">Color</div>
                      <div className="flex-1 text-xs text-gray-400 pl-2">Status name</div>
                      <div className="w-24 text-xs text-gray-400">Category</div>
                      <div className="w-6" />
                    </div>

                    <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                      {form.workflow.statuses.map((status, idx) => (
                        <div
                          key={status.id}
                          draggable
                          onDragStart={() => handleDragStart(idx)}
                          onDragOver={(e) => handleDragOver(e, idx)}
                          onDragEnd={handleDragEnd}
                          className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border transition-all ${
                            dragIdx === idx
                              ? 'border-blue-400 bg-blue-50 opacity-60 scale-95'
                              : errors[`status_${idx}`]
                              ? 'border-red-200 bg-red-50'
                              : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-white'
                          }`}
                        >
                          <GripVertical className="w-4 h-4 text-gray-300 cursor-grab active:cursor-grabbing flex-shrink-0" />
                          <div className="relative flex-shrink-0">
                            <button
                                type="button"
                                onClick={() => {
                                setColorPickerIdx(colorPickerIdx === idx ? null : idx);
                                }}
                                className="w-6 h-6 rounded-full border-2 border-white shadow ring-1 ring-gray-200 hover:ring-gray-400 transition-all flex-shrink-0"
                                style={{ backgroundColor: status.color }}
                                title="Pick color"
                                  />
                            {colorPickerIdx === idx && (
                              <>
                                <div
                                  className="fixed inset-0 z-10"
                                  onClick={() => setColorPickerIdx(null)}
                                />
                                <div className="absolute top-8 left-0 z-20 bg-white border border-gray-200 rounded-xl shadow-xl p-3 w-56">
                                  <div className="relative mb-3">
                                    <input
                                      type="color"
                                      value={status.color}
                                      ref={(el) => { colorInputRefs.current[idx] = el; }}
                                      onChange={(e) => updateStatus(idx, 'color', e.target.value)}
                                      className="w-full h-10 rounded-lg cursor-pointer border border-gray-200 p-0.5"
                                      title="Choose any color"
                                    />
                                    <div
                                      className="absolute inset-0 rounded-lg flex items-center justify-between px-3 cursor-pointer pointer-events-none"
                                      style={{ backgroundColor: status.color + '22' }}
                                    >
                                      <div className="flex items-center gap-2">
                                        <div
                                          className="w-5 h-5 rounded-full border-2 border-white shadow"
                                          style={{ backgroundColor: status.color }}
                                        />
                                        <span className="text-xs font-mono text-gray-700">{status.color}</span>
                                      </div>
                                      <span className="text-xs text-gray-500">Click to pick</span>
                                    </div>
                                  </div>
                                  <p className="text-xs text-gray-400 mb-2">Quick picks</p>
                                  <div className="grid grid-cols-6 gap-1.5 mb-3">
                                    {COLOR_SWATCHES.map((c) => (
                                      <button
                                        key={c}
                                        type="button"
                                        onClick={() => updateStatus(idx, 'color', c)}
                                        className={`w-6 h-6 rounded-md hover:scale-110 transition-transform ring-offset-1 ${
                                          status.color === c ? 'ring-2 ring-blue-500' : 'hover:ring-1 hover:ring-gray-300'
                                        }`}
                                        style={{ backgroundColor: c }}
                                        title={c}
                                      />
                                    ))}
                                  </div>
                                  <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                                    <div
                                      className="w-4 h-4 rounded-full flex-shrink-0 border border-gray-200"
                                      style={{ backgroundColor: status.color }}
                                    />
                                    <input
                                      type="text"
                                      value={status.color}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        // allow typing — only update if valid hex
                                        if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) {
                                          updateStatus(idx, 'color', v);
                                        }
                                      }}
                                      onBlur={(e) => {
                                        if (!/^#[0-9A-Fa-f]{6}$/.test(e.target.value)) {
                                          updateStatus(idx, 'color', status.color);
                                        }
                                      }}
                                      className="flex-1 text-xs font-mono bg-transparent focus:outline-none text-gray-700 min-w-0"
                                      placeholder="#000000"
                                      maxLength={7}
                                      spellCheck={false}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => setColorPickerIdx(null)}
                                      className="text-xs text-blue-600 font-medium hover:text-blue-700 flex-shrink-0"
                                    >
                                      Done
                                    </button>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                          <input
                            type="text"
                            value={status.name}
                            onChange={(e) =>
                              updateStatus(idx, 'name', e.target.value.toUpperCase())
                            }
                            placeholder="STATUS NAME"
                            className={`flex-1 min-w-0 px-2 py-0.5 text-xs font-semibold tracking-wide bg-transparent border-b focus:outline-none transition-colors placeholder-gray-300 ${
                              errors[`status_${idx}`]
                                ? 'border-red-400 text-red-600'
                                : 'border-transparent focus:border-blue-400 text-gray-800'
                            }`}
                          />

                          {/* Category */}
                          <input
                            type="text"
                            value={status.category}
                            onChange={(e) =>
                              updateStatus(idx, 'category', e.target.value.toLowerCase().replace(/\s/g, '_'))
                            }
                            placeholder="category"
                            className="w-24 px-2 py-0.5 text-xs bg-transparent border-b border-transparent focus:outline-none focus:border-blue-400 text-gray-400 placeholder-gray-300 transition-colors"
                          />
                          <button
                            type="button"
                            onClick={() => removeStatus(idx)}
                            disabled={form.workflow.statuses.length <= 1}
                            className="p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-md transition-all disabled:opacity-20 disabled:cursor-not-allowed flex-shrink-0"
                            title="Remove status"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={addStatus}
                      className="mt-2 w-full py-2 text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg hover:border-blue-300 hover:text-blue-500 hover:bg-blue-50 transition-all flex items-center justify-center gap-1"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add another status
                    </button>
                  </>
                )}
              </div>
              {errors.submit && (
                <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-start gap-2">
                  <span className="text-red-500 mt-0.5">⚠</span>
                  <span>{errors.submit}</span>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center gap-3">
          {step === 2 && (
            <button
              type="button"
              onClick={handleBack}
              disabled={isSubmitting}
              className="px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-300 bg-white rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              ← Back
            </button>
          )}

          <div className="flex-1" />

          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-300 bg-white rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>

          {step === 1 ? (
            <button
              type="button"
              onClick={handleNext}
              className="px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors shadow-sm"
            >
              Next: Workflow →
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 active:bg-blue-800 disabled:opacity-60 disabled:cursor-not-allowed transition-colors shadow-sm flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin inline-block" />
                  Creating...
                </>
              ) : (
                'Create Project'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};